import {
    Component,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import {
    AbstractControl,
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { forkJoin, map, of, Subscription, switchMap } from 'rxjs';

import { DiscrepancyDataService } from '@services/data/discrepancy-data.service';
import { DiscrepancyService } from '@services/api/discrepancy/discrepancy.service';
import { DocumentService } from '@services/api/document/document.service';
import { DocumentDataService } from '@services/data/document-data.service';
import { AutomationService } from '@services/api/automation/automation.service';
import { UserService } from '@services/api/user/user.service';
import { UserDataService } from '@services/data/user-data.service';
import { WorkflowDataService } from '@services/data/workflow-data.service';
import { WorkflowService } from '@services/api/workflow/workflow.service';
import { FeatureFlagService } from '@services/feature-flag.service';
import { InputComponent } from '@shared/input/input.component';
import { SelectComponent } from '@shared/select/select.component';
import { LoaderComponent } from '@shared/loader/loader.component';
import { TooltipComponent } from '@shared/tooltip/tooltip.component';
import { CheckboxComponent } from '@shared/checkbox/checkbox.component';
import { TextareaComponent } from '@shared/textarea/textarea.component';
import { ContentFieldComponent } from '@shared/content-field/content-field.component';
import { DatepickerComponent } from '@shared/datepicker/datepicker.component';
import { PrioritySelectComponent } from '@shared/select/priority-select/priority-select.component';
import { PageRangeFormatErrorComponent } from '@shared/validation-error/page-range-format-error.component';
import { PriorityFieldComponent } from '@features/priority-field/priority-field.component';
import { StepperComponent } from '@shared/stepper/stepper.component';
import { PageRangeConflictErrorComponent } from '@shared/validation-error/page-range-conflict-error.component';
import { PageRangeExceedErrorComponent } from '@shared/validation-error/page-range-exceed-error.component';
import { DiscrepancyCollapsiblesComponent } from '@main/discrepancy-collapsibles/discrepancy-collapsibles.component';
import { FooterStateActionsComponent } from './footer-state.actions.component';
import { PageRangeLimitationComponent } from './page-range-limitation.component';
import { FooterAssignActionsComponent } from './footer-assign-actions.component';
import {
    FixerFieldComponent,
    StationFixerOptions,
} from './fixer-field.component';
import { DashIfEmptyPipe } from '@pipes/dash-if-empty.pipe';
import { ErrorResponse } from '@models/api.model';
import { WorkflowState } from '@models/workflow/workflow-state';
import {
    DiscrepancyDetail,
    DiscrepancyEditOptions,
    DiscrepancyFixers,
    DiscrepancyFilterOptions,
    DiscrepancyFilterOptionsQuery,
    PageConflict,
} from '@models/discrepancy';
import {
    AutomationFieldKind,
    AutomationRule,
    AutomationRuleField,
    AutomationRuleTypeGroup,
} from '@models/automation';
import { CustomColumn } from '@models/workflow/custom-column';
import { SelectOption } from '@models/shared.model';
import { DiscrepancyResolutionStateToLabel } from '@main/utils';
import { SubscriptionList, SubscriptionListType } from '@helpers/subscription';
import {
    pageRangeFormatValidator,
    optionExistsValidator,
    pageRangeExceedValidator,
} from '@helpers/validators';
import { deepEqual, isNil } from '@helpers/index';
import {
    CUSTOM_COLUMN_DATE_FORMAT,
    formatCustomColumnValue,
    getCustomColumnInputType,
    getCustomColumnLabel,
    getCustomColumnOptions,
    getCustomColumnRawValue,
    isCustomColumnDate,
    isCustomColumnListBox,
    normalizeCustomColumnValues,
    parseCustomDate,
    toDateValue,
} from '@helpers/custom-column.helpers';
import {
    AutomationTarget,
    automationFieldIdsSharingTarget,
    automationInputFieldIdsOfKind,
    automationInputFieldIdsSharingTarget,
    automationOutputFieldIdsOfKind,
    automationOutputFieldIdsSharingTarget,
    automationInputFields,
    automationOutputFields,
    flattenAutomationRules,
    resolveAutomationTarget,
    toAutomationRuleValue,
    toCustomColumnControlValue,
    AutomationOptionContext,
} from '@helpers/automation-field.helpers';
import {
    findOptionByRuleValue,
    isNameBasedValueKind,
} from '@helpers/automation-option-value';
import {
    AutomationCascadeGraph,
    AutomationCascadeWrite,
    AutomationPreparedWrite,
    buildAutomationCascadeGraph,
    downstreamOutputFields,
    evaluateAutomationCascade,
} from '@helpers/automation-cascade.helpers';
import { ErrorCode } from '@constants/error-code';
import {
    CUSTOM_COLUMN_INT_MAX,
    CUSTOM_COLUMN_INT_MIN,
    CUSTOM_COLUMN_MAX_LENGTH,
    DIRECTIONS_MAX_LENGTH,
} from '@constants/discrepancy-limits';
import DATA_QA from '@automation/data-qa.json';
import { Role } from '@models/user';
import { Status } from '@models/user/status';

type AutomationValue = number | string | number[] | null | undefined;

/** What a cascade write overwrote, and what it put there. */
interface AutomationUndoEntry {
    previous: unknown;
    written: unknown;
}

@Component({
    selector: 'app-discrepancy-details',
    imports: [
        PrioritySelectComponent,
        SelectComponent,
        InputComponent,
        ContentFieldComponent,
        CheckboxComponent,
        DatepickerComponent,
        PageRangeLimitationComponent,
        PageRangeFormatErrorComponent,
        MatDividerModule,
        ReactiveFormsModule,
        DiscrepancyCollapsiblesComponent,
        FooterAssignActionsComponent,
        FooterStateActionsComponent,
        LoaderComponent,
        PriorityFieldComponent,
        TextareaComponent,
        StepperComponent,
        DashIfEmptyPipe,
        FixerFieldComponent,
        PageRangeConflictErrorComponent,
        PageRangeExceedErrorComponent,
        TooltipComponent,
        NgTemplateOutlet,
        MatIconModule,
    ],
    styleUrl: 'discrepancy-details.component.scss',
    templateUrl: 'discrepancy-details.component.html',
})
export class DiscrepancyDetailsComponent
    implements OnInit, OnChanges, OnDestroy
{
    private static readonly CUSTOM_DATE_DISPLAY_FORMAT =
        CUSTOM_COLUMN_DATE_FORMAT;
    private static readonly CUSTOM_COLUMNS_PER_ROW = 2;

    @ViewChild(FooterStateActionsComponent)
    stateActionsComp!: FooterStateActionsComponent;
    @Input() id!: number;
    @Input() mode: 'summary' | 'detail' = 'summary';
    /**
     * Owning document id. Used as a deterministic fallback for resolving the
     * workflow id when the component is not rendered under a routed `id` param
     * (e.g. inside the summary panel), so custom columns load reliably.
     */
    @Input() documentId?: number;
    @Input() allowCollaboratorTakeAssignedDiscrepancy = true;
    @Output() formChange = new EventEmitter();

    form!: FormGroup;
    discDetail = DiscrepancyDetail.initial();
    detailsLoading = false;
    editFormChanged = false;
    editOptions = DiscrepancyEditOptions.initial();
    isAdmin = false;
    isRecordsManager = false;
    isCurrentUserCollaborator = false;
    get isRmInactive() {
        const currentUserId = this.userDataService.currentUser?.id;
        const assignedAnalystId = this.discDetail.analyst?.backingField;
        return this.isRecordsManager && currentUserId !== assignedAnalystId;
    }
    workflowStates: WorkflowState[] = [];
    pageRangeException = false;
    pageRangeConflictData: PageConflict[] = [];
    automationFixerIds: { primary: number | null; secondary: number[] } | null =
        null;
    /** Fixers of the selected station, as loaded by `FixerFieldComponent`. */
    private stationFixerOptions: StationFixerOptions | null = null;
    /** Batches that wrote a fixer before the station's fixers were known. */
    private unverifiedFixerCascade: {
        undo: Map<AbstractControl, AutomationUndoEntry>;
        changedFieldIds: (number | number[])[];
    } | null = null;
    /** Fixer outputs the selected station turned down, for the replay. */
    private readonly rejectedFixerWrites = new Set<string>();
    reasonWarningMessage =
        'The selected option has been removed from the RMS settings. This will not affect discrepancy progress, but consider updating the selection to maintain compliance.';
    readonly maxDirectionsLength = DIRECTIONS_MAX_LENGTH;
    readonly sendOnlySelectedPagesTooltip =
        'This option is available for email stations only. In-app stations always access the full document through the Station Inbox.';
    readonly customColumnMaxLength = CUSTOM_COLUMN_MAX_LENGTH;
    readonly customColumnIntMin = CUSTOM_COLUMN_INT_MIN;
    readonly customColumnIntMax = CUSTOM_COLUMN_INT_MAX;
    private editFormSubscription: Subscription | undefined;
    private _subscriptions = new SubscriptionList() as SubscriptionListType;
    /** Custom column metadata for the current workflow (all columns). */
    customColumns: CustomColumn[] = [];
    /** ListBox dropdown options keyed by lowercased custom column name. */
    private customColumnOptions: Record<string, SelectOption[]> = {};
    readonly dataQa = DATA_QA;
    readonly Status = Status;

    private readonly formBuilder = inject(FormBuilder);
    private readonly discrepancyService = inject(DiscrepancyService);
    private readonly documentService = inject(DocumentService);
    private readonly documentDataService = inject(DocumentDataService);
    private readonly automationService = inject(AutomationService);
    private readonly discrepancyDataService = inject(DiscrepancyDataService);
    private readonly workflowDataService = inject(WorkflowDataService);
    private readonly userService = inject(UserService);
    private readonly userDataService = inject(UserDataService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly workflowService = inject(WorkflowService);
    private readonly route = inject(ActivatedRoute);

    /** Rules grouped by rule type, as chained evaluation needs them. */
    automationRuleGroups: AutomationRuleTypeGroup[] = [];
    /** The same rules flat, for the control listeners and field lookups. */
    private automationRules: AutomationRule[] = [];
    /** Dependency graph of the rule types, rebuilt when the rules load. */
    private automationGraph: AutomationCascadeGraph =
        buildAutomationCascadeGraph([]);
    /**
     * Form value the `editFormChanged` flag is compared against. Held on the
     * instance because the cascade writes with `emitEvent: false` and has to
     * recompute the flag itself once its queue drains.
     */
    private editFormInitialValue: unknown = null;
    /**
     * When true, `applyAutomationRulesOnChange` is a no-op. Set to true while
     * writing back values from the fixer popup so that the popup's explicit
     * user choices are not immediately overwritten by re-running automation.
     */
    private _skipAutomationOnChange = false;
    /**
     * Tracks which output fields have been manually edited by the user, keyed
     * by `fieldId`. When an output field is in this set, automation will not
     * override it, preserving the user's explicit choice.
     */
    private readonly manuallyEditedOutputFields = new Set<number>();
    private readonly fieldKindToControl: Partial<
        Record<AutomationFieldKind, string>
    > = {
        [AutomationFieldKind.Reason]: 'reason',
        [AutomationFieldKind.Station]: 'station',
        [AutomationFieldKind.Fixer]: 'primaryFixer',
        [AutomationFieldKind.SecondaryFixer]: 'secondaryFixers',
        [AutomationFieldKind.Directions]: 'directions',
    };

    get areCommentsEnabled() {
        return this.featureFlagService.getFeature('CommentsSectionEnabled');
    }

    get reason() {
        return this.form.get('reason') as FormControl;
    }

    get stationId() {
        return this.form.get('station') as FormControl;
    }

    get pageRange() {
        return this.form.get('pageRange') as FormControl;
    }

    get readOnly() {
        return !this.editOptions.canEdit;
    }

    get initialReviewDisabledMode() {
        return (
            this.discDetail.status === 'Initial Review' &&
            (this.readOnly ||
                this.isActionsTooltipEnabled() ||
                this.shouldShowTakeActionsTooltip())
        );
    }

    get disabledMode() {
        return this.mode === 'detail' && this.initialReviewDisabledMode;
    }

    get canSave() {
        if (this.isRmInactive) return false;
        if (this.discDetail.analyst == null) return false;
        return !this.form.invalid && this.editFormChanged;
    }

    get canNotify() {
        if (this.isRmInactive) return false;
        const isFormValid = this.form?.valid ?? false;
        const hasPrimaryFixer = !!this.form?.value.primaryFixer;
        if (
            this.userDataService.currentUser?.role === Role.Analyst ||
            this.userDataService.currentUser?.role === Role.Records_Manager
        ) {
            return (
                isFormValid &&
                hasPrimaryFixer &&
                this.discDetail.analyst?.backingField ===
                    this.userDataService.currentUser?.id
            );
        }
        return isFormValid && hasPrimaryFixer;
    }

    get isSecondaryCollaborator(): boolean {
        const currentUserId = this.userDataService.currentUser?.id;
        if (!currentUserId) {
            return false;
        }

        const isCurrentPrimaryAnalyst =
            this.discDetail.analyst?.backingField === currentUserId;

        if (isCurrentPrimaryAnalyst) {
            return false;
        }

        return this.discDetail.secondaryFixers.some(
            (fixer) => fixer.id === currentUserId,
        );
    }

    get completion() {
        const { resolutionState } = this.discDetail;
        return DiscrepancyResolutionStateToLabel[resolutionState!];
    }

    get isInReadOnlyDetailMode(): boolean {
        return (
            this.mode === 'summary' ||
            (this.readOnly && this.discDetail.status !== 'Initial Review')
        );
    }

    get shouldShowInactiveStationError(): boolean {
        return (
            this.isInReadOnlyDetailMode &&
            this.discDetail.status === 'Await Correction' &&
            this.discDetail.stationStatus === Status.Inactive &&
            this.discDetail.stationAssociated
        );
    }

    /** Display priority based on mode and user role */
    get shouldShowPriorityDisplay(): boolean {
        return this.isInReadOnlyDetailMode || !this.isAdmin;
    }

    get shouldShowVersionSelect(): boolean {
        return (
            this.mode === 'detail' &&
            (!this.readOnly || this.initialReviewDisabledMode)
        );
    }

    get customColumnRows(): CustomColumn[][] {
        const rows: CustomColumn[][] = [];
        const colsPerRow = DiscrepancyDetailsComponent.CUSTOM_COLUMNS_PER_ROW;
        for (let i = 0; i < this.customColumns.length; i += colsPerRow) {
            rows.push(this.customColumns.slice(i, i + colsPerRow));
        }
        return rows;
    }

    getCustomColumnLabel(col: CustomColumn): string {
        return getCustomColumnLabel(col);
    }

    ngOnInit() {
        this.listenDiscrepancySave();
        this.listenDiscrepancyReset();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const idChange = changes['id'];

        if (idChange) {
            this.resetDiscrepancyDetails();
        }
    }

    ngOnDestroy() {
        this._subscriptions.unsubscribeAllSafe();
        this.manuallyEditedOutputFields.clear();
    }

    docSizeExceedMaxLimit() {
        return (
            !isNil(this.discDetail.revision) &&
            this.discDetail.revision!.size >
                this.discDetail.revision!.fullFileInclusionLimit
        );
    }

    saveChanges() {
        this.stateActionsComp.onSave();
    }

    onSaveFailed(errorResponse: ErrorResponse) {
        if (errorResponse.code === ErrorCode.PAGE_CONFLICT) {
            this.pageRange.setErrors({
                pageRangeConflict: 'pageRangeConflict',
            });
            this.pageRangeConflictData = errorResponse.data;
        }
    }

    /** Keep the fixers the selected station offers for rule evaluation. */
    onFixerOptionsLoaded(options: StationFixerOptions) {
        // A request completing after the station moved on decides nothing.
        if (options.stationId !== this.stationId.value) return;

        this.stationFixerOptions = options;

        const pending = this.unverifiedFixerCascade;
        if (!pending) return;

        this.unverifiedFixerCascade = null;
        this.rejectedFixerWrites.clear();
        this.replayCascades(pending.changedFieldIds, pending.undo);
    }

    updateFixer(fixers: DiscrepancyFixers) {
        const previousPrimaryFixerId = this.form.value.primaryFixer ?? null;
        const nextPrimaryFixerId = fixers.primaryFixer
            ? fixers.primaryFixer.id
            : null;
        // Rule types declare their own fixer input field, so the popup's
        // single primary fixer control feeds all of them.
        const fixerFieldIds = automationInputFieldIdsOfKind(
            this.automationRules,
            AutomationFieldKind.Fixer,
        );
        // Each rule type can output secondary fixers on its own field id, so
        // every one of them counts as "automation manages this control".
        const secondaryFixerFieldIds = automationOutputFieldIdsOfKind(
            this.automationRules,
            AutomationFieldKind.SecondaryFixer,
        );

        // Suppress automation re-evaluation while writing the user's explicit
        // fixer choices from the popup. Without this guard, both controls would
        // emit and automation could run multiple times with partial state.
        this._skipAutomationOnChange = true;
        this.form.patchValue({
            primaryFixer: nextPrimaryFixerId,
            secondaryFixers: fixers.secondaryFixers.map((i) => i.id),
        });
        this._skipAutomationOnChange = false;

        // Treat popup-selected additional fixers as explicit user intent.
        // This prevents fixer-rule re-evaluation from immediately restoring
        // rule defaults over the user's final selection.
        for (const fieldId of secondaryFixerFieldIds) {
            this.manuallyEditedOutputFields.add(fieldId);
        }

        // Re-evaluate fixer-input automation only when the fixer input changed.
        // If user only edits additional fixers, keep the popup selection as-is.
        if (
            fixerFieldIds.length > 0 &&
            previousPrimaryFixerId !== nextPrimaryFixerId
        ) {
            fixerFieldIds.forEach((fieldId) =>
                this.clearManuallyEditedOutputsForInput(fieldId),
            );
            for (const fieldId of secondaryFixerFieldIds) {
                this.manuallyEditedOutputFields.add(fieldId);
            }
            this.applyAutomationRulesOnChange(fixerFieldIds);

            const currentValues = this.buildCurrentValues();
            const matchOf = (group: AutomationRuleTypeGroup) =>
                this.automationService.matchRules(group, currentValues);
            const isMatch = (
                match: Map<number, string> | null,
            ): match is Map<number, string> => match != null;

            const fixerMatches = this.automationRuleGroups
                .filter((group) =>
                    group.inputFieldIds.some((inputId) =>
                        fixerFieldIds.includes(inputId),
                    ),
                )
                .map(matchOf)
                .filter(isMatch);
            // A chain such as Fixer -> Reason -> SecondaryFixer produces the
            // secondary fixers further down, so the whole reachable chain is
            // inspected and not only the rule types consuming the fixer.
            const hasSecondaryOutput = this.automationGroupsReachableFrom(
                fixerFieldIds,
            )
                .map(matchOf)
                .filter(isMatch)
                .some((match) =>
                    secondaryFixerFieldIds.some((fieldId) =>
                        match.has(fieldId),
                    ),
                );

            // Only clear additional fixers when a fixer rule actually matches
            // but produces no secondary output (i.e. automation is actively
            // managing this fixer and the popup may have returned stale ids).
            // When no fixer rule matches at all, the popup selection reflects
            // the user's explicit choice and must be preserved.
            if (fixerMatches.length > 0 && !hasSecondaryOutput) {
                this._skipAutomationOnChange = true;
                this.form.patchValue({ secondaryFixers: [] });
                this._skipAutomationOnChange = false;
                this.syncAutomationFixerIds({ secondaryFixers: [] });
            }
        }
    }

    private listenDiscrepancySave() {
        this._subscriptions['discrepancy-save-trigger'] =
            this.discrepancyDataService.discrepancySaveTrigger$.subscribe({
                next: () => this.saveChanges(),
            });
    }

    private listenDiscrepancyReset() {
        this._subscriptions['discrepancy-reset-trigger'] =
            this.discrepancyDataService.discrepancyResetTrigger$.subscribe({
                next: () => this.resetDiscrepancyDetails(),
            });
    }

    private resetDiscrepancyDetails() {
        // Reset transient automation state when switching records/reloading.
        this.manuallyEditedOutputFields.clear();
        this.automationFixerIds = null;
        // `stationFixerOptions` is kept: the fixers belong to a station, not
        // to a discrepancy, and the fixer field only reloads them when its
        // own station changes.
        this.unverifiedFixerCascade = null;
        this.rejectedFixerWrites.clear();
        this.initForm();
        this.getDiscrepancyDetails();
        this.setUserPermission();
        this.loadCollaboratorPermission();
        this.getWorkflowStates();
        this.formChange.emit(false);
    }

    canCurrentUserTakeDiscrepancy(): boolean {
        const currentUser = this.userDataService.currentUser;

        if (!currentUser || currentUser.role === null) {
            return false;
        }

        const assignedAnalystId = this.discDetail.analyst?.backingField;

        // Already the assigned analyst — re-taking is meaningless
        if (assignedAnalystId === currentUser.id) {
            return false;
        }

        // Records Manager can always take regardless of assignment
        if (currentUser.role === Role.Records_Manager) {
            return true;
        }

        // Any collaborator can take regardless of role
        if (this.isCurrentUserCollaborator) {
            return true;
        }

        // Non-collaborator Analyst can take only when no analyst is assigned
        if (currentUser.role === Role.Analyst) {
            return assignedAnalystId == null;
        }

        // Other privileged roles (Admin, etc.) can take
        return true;
    }

    private initForm() {
        this.form = this.formBuilder.group({
            priority: new FormControl(null),
            reason: new FormControl(null),
            station: new FormControl(null, [
                optionExistsValidator(() => this.editOptions?.station),
            ]),
            pageRange: new FormControl('', [
                Validators.required,
                pageRangeFormatValidator(),
            ]),
            directions: new FormControl(
                '',
                Validators.maxLength(this.maxDirectionsLength),
            ),
            revision: new FormControl(null),
            notifyWithOnlySelectedPages: new FormControl(null),
            primaryFixer: new FormControl(),
            secondaryFixers: new FormControl([]),
        });
    }

    private getDiscrepancyDetails() {
        this.editFormSubscription?.unsubscribe();
        this.detailsLoading = true;

        this._subscriptions['disc-details'] =
            this.resolveWorkflowIdForAutomation()
                .pipe(
                    switchMap((workflowId) =>
                        forkJoin([
                            this.getEditOptions(this.id),
                            this.discrepancyService.getDiscrepancyDetails(
                                this.id,
                            ),
                            workflowId != null
                                ? this.automationService.getAutomationRules(
                                      workflowId,
                                  )
                                : of([] as AutomationRuleTypeGroup[]),
                            workflowId != null
                                ? this.workflowService.getCustomColumns(
                                      workflowId,
                                  )
                                : of([] as CustomColumn[]),
                            workflowId != null
                                ? this.discrepancyService.getDiscrepanciesFilterOptions(
                                      new DiscrepancyFilterOptionsQuery(
                                          workflowId,
                                      ),
                                  )
                                : of(DiscrepancyFilterOptions.initial()),
                        ]),
                    ),
                )
                .subscribe({
                    next: ([
                        editOptionsData,
                        discDetailsData,
                        rules,
                        customColumns,
                        filterOptions,
                    ]) => {
                        this.editOptions =
                            editOptionsData as DiscrepancyEditOptions;
                        this.discDetail = discDetailsData as DiscrepancyDetail;
                        this.setAutomationRules(
                            rules as AutomationRuleTypeGroup[],
                        );
                        this.setCustomColumns(
                            customColumns as CustomColumn[],
                            editOptionsData as DiscrepancyEditOptions,
                        );
                        this.setStationAndFixer(
                            discDetailsData as DiscrepancyDetail,
                        );
                        this.setDiscrepancyDetails(
                            discDetailsData as DiscrepancyDetail,
                        );
                        this.buildCustomColumnControls(
                            discDetailsData as DiscrepancyDetail,
                        );

                        if (
                            this.mode === 'detail' &&
                            (!this.readOnly || this.initialReviewDisabledMode)
                        ) {
                            this.fillEditForm(
                                discDetailsData as DiscrepancyDetail,
                            );
                            this.addDynamicValidators();
                            this.stationId.updateValueAndValidity();
                            this.touchPageRangeIfEmpty();
                            this.onEditFormValueChange();
                        }
                    },
                    complete: () => (this.detailsLoading = false),
                });
    }

    private resolveWorkflowIdForAutomation() {
        const currentWorkflowId =
            this.documentDataService.currentDocument?.workflowId;
        if (currentWorkflowId != null) {
            return of(currentWorkflowId);
        }

        const documentId = this.documentId ?? this.getDocumentIdFromRoute();
        if (documentId == null) {
            return of(null);
        }

        return this.documentService
            .getDocumentDetails(documentId)
            .pipe(map((doc) => doc?.workflowId ?? null));
    }

    private setStationAndFixer(disc: DiscrepancyDetail) {
        this.discrepancyDataService.updateSelectedDiscStationAndFixer({
            station: disc.station?.backingField ?? null,
            fixer: disc.primaryFixer?.id ?? null,
        });
    }

    private setDiscrepancyDetails(disc: DiscrepancyDetail) {
        this.discrepancyDataService.updateDiscrepancyDetails(disc);
    }

    /** Store custom column metadata and lowercase ListBox option keys. */
    private setCustomColumns(
        columns: CustomColumn[],
        editOptions: DiscrepancyEditOptions,
    ) {
        this.customColumns = columns ?? [];
        const options: Record<string, SelectOption[]> = {};
        Object.entries(editOptions?.customColumns ?? {}).forEach(
            ([key, value]) => {
                options[key.toLowerCase()] = value;
            },
        );
        this.customColumnOptions = options;
    }

    /**
     * Build a nested `customColumns` FormGroup keyed by column name so edited
     * values flow into the save payload via the footer's editForm.value spread.
     *
     * Note: Validators (e.g., Validators.required) are not applied here because
     * the CustomColumn model does not expose field-level validation rules.
     * If backend requires optional/required enforcement, update CustomColumn
     * model to include an `isRequired` property.
     */
    private buildCustomColumnControls(disc: DiscrepancyDetail) {
        if (this.customColumns.length === 0) return;
        const group: Record<string, FormControl> = {};
        this.customColumns.forEach((col) => {
            // Construct validators array if needed in future (e.g., col.isRequired check)
            group[col.columnName] = new FormControl(
                this.getCustomColumnControlValue(col, disc),
                [],
            );
        });
        this.form.setControl('customColumns', this.formBuilder.group(group));
    }

    /** Raw custom column value for this discrepancy (case-insensitive lookup). */
    private rawCustomValue(
        col: CustomColumn,
        disc?: DiscrepancyDetail,
    ): unknown {
        const values = (disc ?? this.discDetail).customColumnValues ?? {};
        return getCustomColumnRawValue(col, values);
    }

    /** Initial FormControl value: ListBox -> backingField, otherwise raw value. */
    private getCustomColumnControlValue(
        col: CustomColumn,
        disc?: DiscrepancyDetail,
    ): unknown {
        const raw = this.rawCustomValue(col, disc);
        if (this.isCustomColumnListBox(col)) {
            return (raw as { backingField?: unknown })?.backingField ?? null;
        }
        if (this.isCustomColumnDate(col)) {
            return this.toDateValue(raw);
        }
        if (this.isCustomColumnNumber(col)) {
            // Seed numeric columns as strings so the initial value matches what
            // the text input emits. Otherwise reverting to the original number
            // is compared as number-vs-string and keeps the form "changed".
            return raw == null ? '' : String(raw);
        }
        return raw ?? '';
    }

    isCustomColumnListBox(col: CustomColumn): boolean {
        return isCustomColumnListBox(col);
    }

    isCustomColumnDate(col: CustomColumn): boolean {
        return isCustomColumnDate(col);
    }

    getCustomColumnInputType(col: CustomColumn): string {
        return getCustomColumnInputType(col);
    }

    isCustomColumnNumber(col: CustomColumn): boolean {
        return this.getCustomColumnInputType(col) === 'number';
    }

    private parseCustomDate(raw: unknown): moment.Moment | null {
        return parseCustomDate(raw);
    }

    private toDateValue(raw: unknown): Date | null {
        return toDateValue(raw);
    }

    getCustomColumnOptions(col: CustomColumn): SelectOption[] {
        return getCustomColumnOptions(col, this.customColumnOptions);
    }

    /**
     * Normalize custom column values: parse dates to ISO 8601, omit empty strings.
     * Called by footer component before sending payload to API.
     */
    normalizeCustomColumnValues(
        customColumns?: Record<string, unknown>,
    ): Record<string, unknown> {
        return normalizeCustomColumnValues(customColumns, this.customColumns);
    }

    /** Read-only display value for a custom column. */
    getCustomColumnDisplay(col: CustomColumn): string {
        const raw = this.rawCustomValue(col);
        return formatCustomColumnValue(
            col,
            raw,
            DiscrepancyDetailsComponent.CUSTOM_DATE_DISPLAY_FORMAT,
        );
    }

    private getEditOptions(id: number) {
        if (this.mode === 'detail') {
            return this.discrepancyService.getDiscrepancyEditOptions(id);
        }
        return of(DiscrepancyEditOptions.initial());
    }

    private fillEditForm({
        priority,
        pageRange,
        reason,
        station,
        directions,
        revision,
        notifyWithOnlySelectedPages,
        primaryFixer,
        secondaryFixers,
    }: DiscrepancyDetail) {
        this.form.patchValue({
            priority,
            pageRange,
            reason: reason?.backingField,
            station: station?.backingField,
            directions,
            revision: revision?.id,
            notifyWithOnlySelectedPages,
            primaryFixer: primaryFixer?.id ?? null,
            secondaryFixers: secondaryFixers.map((i) => i.id),
        });
    }

    private onEditFormValueChange() {
        this.editFormChanged = false;
        this.editFormInitialValue = this.form.value;

        this.editFormSubscription = this.form.valueChanges.subscribe(() => {
            this.emitEditFormChanged();
        });
        this.handleStationChange();
        this.handlePageRangeChange();
        this.handleRevisionChange();
        this.setupAutomationRuleListeners();
    }

    private setUserPermission() {
        this._subscriptions['is-admin-user'] = this.userService
            .isAdminUser()
            .subscribe({
                next: (isAdmin) => {
                    this.isAdmin = isAdmin;
                    this.isRecordsManager =
                        this.userDataService.currentUser?.role ===
                        Role.Records_Manager;
                },
            });
    }

    private getWorkflowStates() {
        this._subscriptions['workflow-states'] =
            this.workflowDataService.states$.subscribe({
                next: (states) => (this.workflowStates = states),
            });
    }

    private handleStationChange(): void {
        this._subscriptions['station-change'] = this.form.controls[
            'station'
        ].valueChanges.subscribe(() => {
            this.form.patchValue(
                { primaryFixer: null, secondaryFixers: [] },
                { emitEvent: false },
            );
            this.stationId.updateValueAndValidity({ emitEvent: false });
        });
    }

    private handlePageRangeChange(): void {
        this._subscriptions['page-range-change'] = this.form.controls[
            'pageRange'
        ].valueChanges.subscribe(() => {
            const { pageRangeConflict, ...restErrors } =
                this.pageRange.errors || {};
            this.pageRange.setErrors(
                Object.keys(restErrors).length ? restErrors : null,
            );
        });
    }

    private addDynamicValidators(): void {
        if (this.discDetail.revision?.pageSize) {
            this.pageRange.addValidators([
                pageRangeExceedValidator(
                    () => this.discDetail.revision?.pageSize ?? null,
                ),
            ]);
            this.pageRange.updateValueAndValidity({ emitEvent: false });
        }
    }

    private touchPageRangeIfEmpty(): void {
        if (!this.pageRange.value) {
            this.pageRange.markAsTouched();
            this.pageRange.updateValueAndValidity({ emitEvent: false });
        }
    }

    private handleRevisionChange(): void {
        this._subscriptions['revision-change'] = this.form.controls[
            'revision'
        ].valueChanges.subscribe(() => {
            // Re-run exceed validator when revision (and thus page size) changes.
            this.pageRange.updateValueAndValidity();
        });
    }

    private cleanupAutomationSubscriptions(): void {
        Object.keys(this._subscriptions).forEach((key) => {
            if (key.startsWith('automation-')) {
                this._subscriptions.unsubscribeSafe(key);
            }
        });
    }

    private setupAutomationRuleListeners(): void {
        if (this.automationRules.length === 0) return;

        // Cleanup existing automation subscriptions before setting up new ones
        this.cleanupAutomationSubscriptions();

        const outputFields = automationOutputFields(this.automationRules);

        automationInputFields(this.automationRules).forEach(
            (field, fieldId) => {
                const target = this.resolveAutomationTarget(field);
                if (!target) return;

                // One control can be the input of several rule types under
                // different field ids; the first of them carries the
                // subscription and triggers all of them together.
                const sharedFieldIds = automationInputFieldIdsSharingTarget(
                    this.automationRules,
                    field,
                );
                if (sharedFieldIds[0] !== fieldId) return;

                // Output fields the same control writes to, whatever field id
                // their rule type declares for it.
                const sharedOutputFieldIds =
                    automationOutputFieldIdsSharingTarget(
                        this.automationRules,
                        field,
                    );

                this._subscriptions[`automation-input-${fieldId}`] =
                    target.control.valueChanges.subscribe(() => {
                        // When an input changes, clear manually-edited status from its outputs.
                        // This allows automation to re-evaluate those outputs on input change.
                        sharedFieldIds.forEach((sharedId) =>
                            this.clearManuallyEditedOutputsForInput(sharedId),
                        );
                        // A control that is an input as well as an output keeps
                        // the value the user just typed: this listener runs
                        // before the output listener below and after the
                        // clearing above, so the protection has to be set here.
                        sharedOutputFieldIds.forEach((outputId) =>
                            this.manuallyEditedOutputFields.add(outputId),
                        );
                        this.applyAutomationRulesOnChange(sharedFieldIds);
                    });
            },
        );

        // Listen for changes to output fields to detect manual user edits.
        outputFields.forEach((field, fieldId) => {
            const target = this.resolveAutomationTarget(field);
            if (!target) return;

            this._subscriptions[`automation-output-${fieldId}`] =
                target.control.valueChanges.subscribe(() => {
                    // User manually edited an output field.  Mark it so automation
                    // won't override it (unless the input that generates it changes).
                    this.manuallyEditedOutputFields.add(fieldId);
                });
        });
    }

    private resolveAutomationTarget(
        field: AutomationRuleField,
    ): AutomationTarget | null {
        return resolveAutomationTarget(
            field,
            this.form,
            this.fieldKindToControl,
            this.customColumns,
        );
    }

    /** Keep the flat rules, the grouping and the dependency graph in sync. */
    private setAutomationRules(groups: AutomationRuleTypeGroup[]): void {
        this.automationRuleGroups = groups;
        this.automationRules = flattenAutomationRules(groups);
        this.automationGraph = buildAutomationCascadeGraph(groups, (fieldId) =>
            this.automationFieldIdsSharingTarget(fieldId),
        );
    }

    /**
     * Run the cascade the changed field starts and write everything it
     * derives. A field filled here triggers the rule types consuming it, so a
     * chain of any length is filled in one go.
     *
     * Writes keep using `emitEvent: false` (an event per patch would
     * re-trigger the cascade), so the `editFormChanged` flag the footer gates
     * Save on is recomputed once after the queue drains.
     */
    private applyAutomationRulesOnChange(
        changedFieldId: number | number[],
    ): void {
        // A fixer the station turned down is only known to be one after the
        // batch settled the station, and that verdict belongs to this change.
        this.rejectedFixerWrites.clear();
        this.runAutomationCascade(changedFieldId);
    }

    private runAutomationCascade(changedFieldId: number | number[]): void {
        if (
            this.automationRuleGroups.length === 0 ||
            this._skipAutomationOnChange
        )
            return;

        const writes = evaluateAutomationCascade({
            groups: this.automationRuleGroups,
            graph: this.automationGraph,
            changedFieldId,
            currentValues: this.buildCurrentValues(),
            host: {
                matchRules: (group, currentValues) =>
                    this.automationService.matchRules(group, currentValues),
                prepareWrite: (fieldId, ruleValue) =>
                    this.prepareAutomationWrite(fieldId, ruleValue),
                isWriteSuppressed: (fieldId) =>
                    this.manuallyEditedOutputFields.has(fieldId),
                fieldIdsSharingTarget: (fieldId) =>
                    this.automationFieldIdsSharingTarget(fieldId),
            },
        });
        if (writes.length === 0) return;

        const patchValues: Record<string, any> = {};
        // Value every written control held, so the whole batch can be undone
        // when a fixer of it turns out not to belong to the station.
        const undo = new Map<AbstractControl, AutomationUndoEntry>();

        writes.forEach(({ fieldId, controlValue }) => {
            const output = this.resolveAutomationOutput(fieldId);
            if (!output) return;

            const control = output.target.control;
            // Two rule types can write one control; the undo keeps what it
            // held before the first of them and what the last one put there.
            const captured = undo.get(control);
            if (captured) captured.written = controlValue;
            else
                undo.set(control, {
                    previous: control.value,
                    written: controlValue,
                });

            if (output.target.column) {
                control.patchValue(controlValue, { emitEvent: false });
                return;
            }
            patchValues[output.controlName] = controlValue;
        });

        if (Object.keys(patchValues).length > 0) {
            this.form.patchValue(patchValues, { emitEvent: false });
            this.syncAutomationFixerIds(patchValues);
        }

        // The cascade only ever starts from a user change, so recomputing here
        // enables Save as a result of that change, never on load.
        this.emitEditFormChanged();
        this.settleFixerWrites(writes, changedFieldId, undo);
    }

    /**
     * Check the fixers the batch wrote against the station it settled on.
     *
     * The batch is replayed without a fixer the station does not hold, rather
     * than only stripping it: rule types consuming that fixer already derived
     * their own fields from it, and those must not survive it. The fixers of
     * the station are loaded asynchronously, so a batch that outran them waits
     * for `onFixerOptionsLoaded` instead.
     */
    private settleFixerWrites(
        writes: AutomationCascadeWrite[],
        changedFieldId: number | number[],
        undo: Map<AbstractControl, AutomationUndoEntry>,
    ): void {
        const outputs = automationOutputFields(this.automationRules);
        const fieldKindOf = ({ fieldId }: AutomationCascadeWrite) =>
            outputs.get(fieldId)?.fieldKind;
        if (!writes.some((write) => this.isFixerKind(fieldKindOf(write))))
            return;

        if (!this.hasStationFixerOptions) {
            this.rememberUnverifiedCascade(changedFieldId, undo);
            return;
        }

        const rejected = writes.filter((write) => {
            const fieldKind = fieldKindOf(write);
            return (
                this.isFixerKind(fieldKind) &&
                !this.isStationFixer(
                    fieldKind,
                    write.controlValue as AutomationValue,
                )
            );
        });
        if (rejected.length === 0) return;

        // `isRejectedFixerWrite` suppresses a rejected output on every path
        // that produces a write, so each replay strips one more of a finite
        // set and the chain ends.
        rejected.forEach(({ fieldId, controlValue }) =>
            this.rejectedFixerWrites.add(
                this.fixerWriteKey(fieldId, controlValue),
            ),
        );
        this.replayCascades([changedFieldId], undo);
    }

    /**
     * Keep a batch whose fixers could not be checked, together with the values
     * it overwrote, so it can be replayed once the station's fixers arrive.
     * An earlier batch still waiting keeps its own undo values: the replay has
     * to start from the state before the first of them.
     */
    private rememberUnverifiedCascade(
        changedFieldId: number | number[],
        undo: Map<AbstractControl, AutomationUndoEntry>,
    ): void {
        const pending = this.unverifiedFixerCascade;
        if (!pending) {
            this.unverifiedFixerCascade = {
                undo,
                changedFieldIds: [changedFieldId],
            };
            return;
        }

        undo.forEach((entry, control) => {
            const earlier = pending.undo.get(control);
            if (earlier) earlier.written = entry.written;
            else pending.undo.set(control, entry);
        });
        pending.changedFieldIds.push(changedFieldId);
    }

    /**
     * Restore what the batches wrote and run them again. A control the user
     * edited in the meantime no longer holds what automation wrote, so their
     * own value is left alone.
     */
    private replayCascades(
        changedFieldIds: (number | number[])[],
        undo: Map<AbstractControl, AutomationUndoEntry>,
    ): void {
        undo.forEach(({ previous, written }, control) => {
            if (!deepEqual(control.value, written)) return;
            control.patchValue(previous, { emitEvent: false });
        });
        this.refreshAutomationFixerIds();
        this.emitEditFormChanged();

        changedFieldIds.forEach((changedFieldId) =>
            this.runAutomationCascade(changedFieldId),
        );
    }

    /** Whether a rule output targets one of the two fixer controls. */
    private isFixerKind(
        fieldKind: AutomationFieldKind | undefined,
    ): fieldKind is AutomationFieldKind {
        return (
            fieldKind === AutomationFieldKind.Fixer ||
            fieldKind === AutomationFieldKind.SecondaryFixer
        );
    }

    /**
     * Whether the station already turned this exact fixer output down. Checked
     * on every path that produces a write, so a rejected output is always
     * suppressed on the replay and the chain of replays ends.
     */
    private isRejectedFixerWrite(
        fieldId: number,
        controlValue: unknown,
    ): boolean {
        return this.rejectedFixerWrites.has(
            this.fixerWriteKey(fieldId, controlValue),
        );
    }

    /** Identity of a fixer output the station turned down. */
    private fixerWriteKey(fieldId: number, controlValue: unknown): string {
        const ids = Array.isArray(controlValue) ? controlValue : [controlValue];
        return `${fieldId}:${ids.join(',')}`;
    }

    /**
     * Rule value coerced for the field's control, or `null` when it cannot be
     * used and the current value must be kept.
     */
    private prepareAutomationWrite(
        fieldId: number,
        ruleValue: string,
    ): AutomationPreparedWrite | null {
        const resolved = this.resolveAutomationOutput(fieldId);
        if (!resolved) return null;

        const { field, target } = resolved;

        // A blank rule output clears the control: the coercions below would
        // turn it into `0` for a select, `[0]` for the fixer list or skip it
        // for a list box column.
        if (ruleValue.trim() === '') {
            const cleared = target.column
                ? this.getEmptyValueForColumn(target.column)
                : this.getEmptyValueForKind(field.fieldKind);
            return this.isRejectedFixerWrite(fieldId, cleared)
                ? null
                : { controlValue: cleared, canonical: null };
        }

        const optionContext = this.automationOptionContext(field, target);
        const converted = target.column
            ? toCustomColumnControlValue(
                  target.column,
                  ruleValue,
                  this.getCustomColumnOptions(target.column),
                  field.valueKind,
              )
            : this.convertAutomationValue(field, ruleValue, optionContext);
        if (converted === undefined) return null;
        // Rules are defined over every repository's options, so an output may
        // not exist for this discrepancy's repository.
        if (
            !target.column &&
            !this.isAutomationValueSelectable(
                field.fieldKind,
                converted as AutomationValue,
            )
        )
            return null;
        // Skipping the write here also keeps the rule types consuming this
        // fixer from running, so nothing is derived from it either.
        if (this.isRejectedFixerWrite(fieldId, converted)) return null;

        return {
            controlValue: converted,
            canonical: toAutomationRuleValue(target, converted, optionContext),
        };
    }

    /** Output field, its resolved target and its standard control name. */
    private resolveAutomationOutput(fieldId: number): {
        field: AutomationRuleField;
        target: AutomationTarget;
        controlName: string;
    } | null {
        const field = automationOutputFields(this.automationRules).get(fieldId);
        const target = field && this.resolveAutomationTarget(field);
        if (!field || !target) return null;

        return {
            field,
            target,
            controlName: this.fieldKindToControl[field.fieldKind] ?? '',
        };
    }

    /**
     * When an input field changes, clear the "manually edited" flag from every
     * output field the change can re-derive. The protection is cleared
     * transitively along the whole chain, so a change at its head re-derives
     * the fields further down as well.
     */
    private clearManuallyEditedOutputsForInput(inputFieldId: number): void {
        downstreamOutputFields(
            this.automationRuleGroups,
            inputFieldId,
            (fieldId) => this.automationFieldIdsSharingTarget(fieldId),
        ).forEach((fieldId) => {
            // The changed field itself keeps its protection: the user edited
            // it, so automation must not derive it again from its own chain.
            if (fieldId === inputFieldId) return;
            this.manuallyEditedOutputFields.delete(fieldId);
        });
    }

    /**
     * Rule types the change of `fieldId` can reach: the ones consuming it plus
     * the ones consuming a field derived from it, transitively.
     */
    private automationGroupsReachableFrom(
        fieldIds: number[],
    ): AutomationRuleTypeGroup[] {
        const derivedFields = new Set(
            fieldIds.flatMap((fieldId) =>
                downstreamOutputFields(
                    this.automationRuleGroups,
                    fieldId,
                    (id) => this.automationFieldIdsSharingTarget(id),
                ),
            ),
        );

        return this.automationRuleGroups.filter(
            (group) =>
                group.inputFieldIds.some((inputId) =>
                    fieldIds.includes(inputId),
                ) ||
                group.inputFieldIds.some((inputId) =>
                    derivedFields.has(inputId),
                ),
        );
    }

    /** Recompute the flag the footer gates Save on and publish it once. */
    private emitEditFormChanged(): void {
        this.editFormChanged = !deepEqual(
            this.form.getRawValue(),
            this.editFormInitialValue,
        );
        this.formChange.emit(this.editFormChanged);
    }

    /**
     * `FixerFieldComponent` renders from its own `value` Input rather than the
     * form, so a plain `form.patchValue` for `primaryFixer`/`secondaryFixers`
     * does not update its display. Forward the IDs to it so it can resolve
     * matching `UserInfo` from its loaded fixer options.
     */
    private syncAutomationFixerIds(patchValues: Record<string, any>): void {
        if (
            !('primaryFixer' in patchValues) &&
            !('secondaryFixers' in patchValues)
        )
            return;

        this.refreshAutomationFixerIds();
    }

    /** Publish the fixer ids the form holds now. */
    private refreshAutomationFixerIds(): void {
        this.automationFixerIds = {
            primary: this.form.value.primaryFixer ?? null,
            secondary: this.form.value.secondaryFixers ?? [],
        };
    }

    private buildCurrentValues(): Map<number, string | null> {
        const values = new Map<number, string | null>();
        const fields = new Map([
            ...automationInputFields(this.automationRules),
            ...automationOutputFields(this.automationRules),
        ]);

        fields.forEach((field, fieldId) => {
            const target = this.resolveAutomationTarget(field);
            if (!target) return;
            values.set(
                fieldId,
                toAutomationRuleValue(
                    target,
                    target.control.value,
                    this.automationOptionContext(field, target),
                ),
            );
        });

        return values;
    }

    /** Field ids this screen renders with the same control as `fieldId`. */
    private automationFieldIdsSharingTarget(fieldId: number): number[] {
        return automationFieldIdsSharingTarget(this.automationRules, fieldId);
    }

    /**
     * What the rules of a field mean by an option value, and the options this
     * discrepancy offers for it.
     */
    private automationOptionContext(
        field: AutomationRuleField,
        target: AutomationTarget,
    ): AutomationOptionContext {
        return {
            valueKind: field.valueKind,
            options: target.column
                ? this.getCustomColumnOptions(target.column)
                : this.standardFieldOptions(field.fieldKind),
        };
    }

    /** Options the screen offers for a standard field kind, if it has any. */
    private standardFieldOptions(
        fieldKind: AutomationFieldKind,
    ): SelectOption[] {
        if (fieldKind === AutomationFieldKind.Reason)
            return this.editOptions.reason ?? [];
        if (fieldKind === AutomationFieldKind.Station)
            return this.editOptions.station ?? [];

        return [];
    }

    private convertAutomationValue(
        field: AutomationRuleField,
        value: string,
        optionContext: AutomationOptionContext,
    ): AutomationValue | undefined {
        const fieldKind = field.fieldKind;

        // A name is the option of whichever repository reads the same, so it is
        // resolved against this discrepancy's options rather than parsed.
        if (isNameBasedValueKind(optionContext.valueKind)) {
            return findOptionByRuleValue(
                optionContext.options ?? [],
                value,
                optionContext.valueKind,
            )?.value;
        }

        if (fieldKind === AutomationFieldKind.SecondaryFixer) {
            return value
                .split(',')
                .map((id) => id.trim())
                .filter((id) => id !== '')
                .map(Number)
                .filter((id) => !Number.isNaN(id));
        }
        if (
            fieldKind === AutomationFieldKind.Reason ||
            fieldKind === AutomationFieldKind.Station ||
            fieldKind === AutomationFieldKind.Fixer
        ) {
            return Number(value);
        }
        return value;
    }

    /**
     * Automation rules are configured against the union of every repository's
     * options, so a rule output can be invalid for the current discrepancy's
     * repository. Such values must be skipped (keeping the current selection)
     * instead of being patched in and rendering as an empty dropdown.
     */
    private isAutomationValueSelectable(
        fieldKind: AutomationFieldKind,
        value: AutomationValue,
    ): boolean {
        const options = this.standardFieldOptions(fieldKind);
        // No option list loaded => nothing to validate against.
        if (options.length === 0) return true;
        return options.some((option) => option.value === value);
    }

    /**
     * Whether the selected station holds the fixer(s) a rule produced. Fixer
     * rules are configured over every station, so an output can name a user
     * this station has no association with, and saving such an id fails in the
     * backend. Only called once the station of the batch is settled and its
     * fixers are the loaded ones.
     */
    private isStationFixer(
        fieldKind: AutomationFieldKind,
        value: AutomationValue,
    ): boolean {
        const loaded = this.stationFixerOptions;
        if (!loaded) return true;

        const ids = (Array.isArray(value) ? value : [value]).filter(
            (id) => id !== null && id !== undefined,
        );
        // A rule blanking the field clears it, which needs no fixer to back it.
        if (ids.length === 0) return true;

        const options =
            fieldKind === AutomationFieldKind.Fixer
                ? loaded.primary
                : loaded.all;
        return ids.every((id) => options.some((fixer) => fixer.id === id));
    }

    /** Whether the loaded fixers are the ones of the selected station. */
    private get hasStationFixerOptions(): boolean {
        return this.stationFixerOptions?.stationId === this.stationId.value;
    }

    private getEmptyValueForKind(
        fieldKind: AutomationFieldKind,
    ): AutomationValue {
        if (fieldKind === AutomationFieldKind.Directions) return '';
        if (fieldKind === AutomationFieldKind.SecondaryFixer) return [];
        return null;
    }

    /** Value clearing a custom column control, per its input type. */
    private getEmptyValueForColumn(column: CustomColumn): AutomationValue {
        return isCustomColumnListBox(column) || isCustomColumnDate(column)
            ? null
            : '';
    }

    isDiscFieldValueInvalid(
        field: keyof (DiscrepancyEditOptions & DiscrepancyDetail),
    ): boolean {
        type FieldValue = { backingField?: unknown };
        const discField = (
            this.discDetail as unknown as Record<string, unknown>
        )[field] as FieldValue | null;
        const value = discField?.backingField;
        if (value === null || value === undefined) return false;

        const options = ((
            this.editOptions as unknown as Record<string, unknown>
        )[field] ?? []) as SelectOption[];
        const optionValues = options.map((o) => o.value);
        return !optionValues.includes(value as number);
    }

    isStationValueInvalid(): boolean {
        return this.isDiscFieldValueInvalid('station');
    }

    isReasonValueInvalid(): boolean {
        return this.isDiscFieldValueInvalid('reason');
    }

    showStationError(): boolean {
        return (
            this.isStationValueInvalid() &&
            this.mode === 'detail' &&
            this.discDetail.status !== 'Apply Correction' &&
            this.discDetail.status !== 'Recently Completed'
        );
    }

    isActionsTooltipEnabled(): boolean {
        const currentUserId = this.userDataService.currentUser?.id;
        const assignedAnalystId = this.discDetail.analyst?.backingField;
        const isAssignedToAnotherUser =
            assignedAnalystId != null &&
            currentUserId != null &&
            assignedAnalystId !== currentUserId;
        const isUnassigned = assignedAnalystId == null;
        const isNotAssignedToCurrentUser =
            isAssignedToAnotherUser || isUnassigned;

        return (
            this.discDetail.status === 'Initial Review' &&
            !this.isSecondaryCollaborator &&
            isNotAssignedToCurrentUser &&
            (!this.canCurrentUserTakeDiscrepancy() || this.isRecordsManager)
        );
    }

    shouldShowTakeActionsTooltip(): boolean {
        const currentUserId = this.userDataService.currentUser?.id;
        const assignedAnalystId = this.discDetail.analyst?.backingField;
        const isAssignedToAnotherUser =
            assignedAnalystId != null &&
            currentUserId != null &&
            assignedAnalystId !== currentUserId;
        const isUnassigned = assignedAnalystId == null;
        const isNotAssignedToCurrentUser =
            isAssignedToAnotherUser || isUnassigned;

        return (
            this.discDetail.status === 'Initial Review' &&
            !this.isSecondaryCollaborator &&
            isNotAssignedToCurrentUser &&
            this.canCurrentUserTakeDiscrepancy() &&
            !this.hasActionsEnabledWithoutTaking()
        );
    }

    /**
     * True when the current user can already perform footer actions without
     * taking the discrepancy. Admin / Host Admin (not Records Manager) can act
     * on a discrepancy assigned to another user, so they must not be prompted
     * to "take or assign" it to enable actions.
     */
    private hasActionsEnabledWithoutTaking(): boolean {
        return (
            this.isAdmin &&
            !this.isRecordsManager &&
            this.discDetail.analyst !== null
        );
    }

    tooltipTextType(): 'take' | 'cannotTake' {
        if (this.canCurrentUserTakeDiscrepancy()) {
            return 'take';
        } else {
            return 'cannotTake';
        }
    }

    /**
     * Single source of truth for whether the footer-actions wrapper tooltip is
     * active. While it is active it is the only tooltip allowed to explain why
     * the footer actions are blocked, so every per-button (child) tooltip must
     * be suppressed to avoid two conflicting messages showing at once.
     */
    isFooterActionsTooltipEnabled(): boolean {
        return (
            this.isActionsTooltipEnabled() ||
            this.shouldShowTakeActionsTooltip() ||
            (this.isRecordsManager &&
                this.isRmInactive &&
                this.discDetail.status !== 'Initial Review')
        );
    }

    private loadCollaboratorPermission() {
        const currentUser = this.userDataService.currentUser;

        if (!currentUser || currentUser.role === null) {
            this.isCurrentUserCollaborator = false;
            return;
        }

        if (currentUser.role !== Role.Analyst) {
            // Records Manager and other privileged roles are handled by early
            // returns in canCurrentUserTakeDiscrepancy; flag is not consulted.
            this.isCurrentUserCollaborator = false;
            return;
        }

        const documentId = this.getDocumentIdFromRoute();
        if (documentId === null) {
            this.isCurrentUserCollaborator = false;
            return;
        }

        this._subscriptions['document-collaborators'] = this.documentService
            .getCollaborators(documentId)
            .subscribe({
                next: ({ collaborators }) => {
                    this.isCurrentUserCollaborator = collaborators.some(
                        (collaborator) => collaborator.id === currentUser.id,
                    );
                },
                error: () => {
                    this.isCurrentUserCollaborator = false;
                },
            });
    }

    private getDocumentIdFromRoute(): number | null {
        let currentRoute: ActivatedRoute | null = this.route;

        while (currentRoute) {
            const routeValue = currentRoute.snapshot.paramMap.get('id');
            if (routeValue !== null) {
                const documentId = Number(routeValue);
                if (!isNaN(documentId)) {
                    return documentId;
                }
            }

            currentRoute = currentRoute.parent;
        }

        return null;
    }

    get pageSizeLimit() {
        return this.discDetail.revision?.pageSize ?? null;
    }
}
